<script setup lang="ts">
/* TODO :: [AF-289] - Component with several limitations
 * This component is throwing a few warning message. It is a limitation
 * of the library we are using and no clear solution in the doccumenation at this moment.
 * We may need to create a component from scratch
 */
import { computed, getCurrentInstance, onBeforeMount, onMounted, ref } from 'vue';
import { useSessionStore } from '@Stores';
import type { FieldSchema } from '../../../types';
import type { Coordinates, GMapAutocompleteModel } from './types';
import { useLazyGoogleMaps } from '@Composables';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{
    coordinates?: Coordinates;
    disabled?: boolean;
    error?: string;
    field?: FieldSchema;
    height?: string;
    placeholder?: string;
    required?: boolean;
    zoom?: number;
  }>(),
  {
    height: '250px',
    placeholder: 'components.forms.placeholder.addressAutocomplete',
    zoom: 12,
  }
);

const { shelterProfile } = useSessionStore();

const defaultCoordinates = computed(() => {
  return shelterProfile?.location
    ? {
        lat: shelterProfile.location.coordinates[1],
        lng: shelterProfile.location.coordinates[0],
      }
    : { lat: 40.701275249835156, lng: -73.99033497545578 };
});

const model = defineModel<GMapAutocompleteModel | string>();

const autocompleteRef = ref();
const coordinates = ref(props.coordinates ?? defaultCoordinates.value);
const zoom = ref(props?.field?.settings.zoom || props.zoom);

onBeforeMount(() => useLazyGoogleMaps(getCurrentInstance()?.appContext.app!));
onMounted(() => initModel());

function initModel() {
  if (!model.value) return;

  const address = (model.value as GMapAutocompleteModel)?.formatted_address;
  autocompleteRef.value.$refs.input.value = address;

  updateModel(model.value);
}

function emptyModels(address?: string) {
  if (address) return;

  autocompleteRef.value.$refs.input.value = '';
  coordinates.value = defaultCoordinates.value;
  model.value = undefined;
}

function updateModel(address: any) {
  zoom.value = 18;
  coordinates.value = {
    lat: address?.geometry?.location?.lat || Number(address?.geometry?.location?.lat()),
    lng: address?.geometry?.location?.lng || Number(address?.geometry?.location?.lng()),
  };

  model.value = address;
}
</script>

<template>
  <div :data-has-error="!!error">
    <GMapAutocomplete
      ref="autocompleteRef"
      :placeholder="$t(placeholder)"
      @place_changed="(place: any) => updateModel(place)"
      @input="(event: any) => emptyModels(event.target?.value)"
    />

    <GMapMap
      v-if="coordinates"
      class="mt-4"
      :zoom
      :center="coordinates"
      :style="{ height: height }"
    >
      <GMapMarker
        icon="https://maps.google.com/mapfiles/ms/icons/green-dot.png"
        :position="coordinates"
      />
    </GMapMap>
  </div>
</template>

<style>
.pac-container {
  z-index: 10000;
}

/* Apply the base styles to the GMapAutocomplete input */
[data-has-error='false'] input,
[data-has-error='true'] input {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background-color: #ffffff;
  color: #111827;
}

/* Error state styles */
[data-has-error='true'] input {
  border-color: #ef4444 !important;
  color: #ef4444 !important;
}

/* Focus state */
[data-has-error='false'] input:focus,
[data-has-error='true'] input:focus {
  outline: none;
  border-color: #369694;
  box-shadow: 0 0 0 0.5px #369694;
}

/* Error focus state */
[data-has-error='true'] input:focus {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 0.5px #ef4444 !important;
}
</style>
